import { Button, Divider, Select, Tabs, Tooltip, notification } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  editionActionButtonStyle,
  editionActionsInnerStyle,
  editionActionsRowStyle,
} from './editionActionsLayout';
import MediaDetailsEdition from './MediaDetailsEdition';

import { useGetMediaTags } from '../../../../../utils/api/medias/useGetMediaTags';
import { useUpdateTagsMediasToQualify } from '../../../../../utils/api/medias/useUpdateTagsMediasToQualify';
import { useUpdateStatusMediasToQualify } from '../../../../../utils/api/medias/useUpdateStatusMediasToQualify';

const BULK_NOTICE_BG = '#f5f5f5';
const BULK_NOTICE_TEXT = '#1677ff';

const bulkLabelStyle = {
  display: 'block',
  marginBottom: 6,
  fontSize: 12,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.65)',
};

const BulkEditionPanel = ({ medias }) => {
  const n = medias.length;
  const scopeLabel =
    n <= 1 ? 'au média sélectionné' : `aux ${n} médias sélectionnés`;
  const mediaNamesTooltip = medias
    .map((m) => m.name?.trim() || '—')
    .filter((m) => m != '—')
    .slice(0, 10)
    .join(', ');

  const selectionKey = useMemo(
    () => medias.map((m) => m?.id ?? '').join(','),
    [medias]
  );

  const [searchedTag, setSearchedTag] = useState('');
  const [debouncedSearchTag, setDebouncedSearchTag] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTag(searchedTag.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchedTag]);

  const { tags, isLoading: isTagLoading } = useGetMediaTags({
    search: debouncedSearchTag,
  });

  const tagOptions = useMemo(() => {
    return tags?.map((tag) => ({
      value: tag.name,
      label: tag.name,
    }));
  }, [tags]);

  const [tagValues, setTagValues] = useState([]);
  const oldTagValues = [];

  useEffect(() => {
    setTagValues([]);
  }, [selectionKey]);

  const handleTagsChange = (value) => {
    setSearchedTag('');
    setTagValues(value);
  };

  const { updateTagsMediasToQualify } = useUpdateTagsMediasToQualify();
  const { updateStatusMediasToQualify } = useUpdateStatusMediasToQualify();

  const [savingMedias, setSavingMedias] = useState(false);
  const [savingAndSendingMedias, setSavingAndSendingMedias] = useState(false);

  const handleSave = useCallback(
    async (oldTags, newTags) => {
      setSavingMedias(true);
      try {
        await updateTagsMediasToQualify({
          old_tags: oldTags,
          new_tags: newTags,
          medias: medias,
        });
        notification.success({
          message: 'Enregistrement',
          description: 'Les modifications ont été enregistrées',
          placement: 'bottomRight',
          duration: 3,
        });
      } catch (e) {
        notification.error({
          message: "Erreur d'enregistrement",
          description: `Les modifications n'ont pas pu être enregistrées : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
      setSavingMedias(false);
    },
    [medias, updateTagsMediasToQualify]
  );

  const handleSaveAndSubmit = useCallback(
    async (oldTags, newTags) => {
      setSavingAndSendingMedias(true);
      try {
        await updateTagsMediasToQualify({
          old_tags: oldTags,
          new_tags: newTags,
          medias: medias,
        });

        const result = await updateStatusMediasToQualify({
          status: 'to_validate',
          mediaIds: medias.map((media) => media.id),
        });

        if (result?.errors?.length > 0) {
          throw new Error(result.errors.map((e) => e.error).join('; '));
        }

        notification.success({
          message: 'Enregistrement et envoi en validation effectués',
          description: 'Les modifications ont été enregistrées',
          placement: 'bottomRight',
          duration: 3,
        });
      } catch (e) {
        notification.error({
          message: "Erreur d'enregistrement",
          description: `Les modifications n'ont pas pu être enregistrées : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
      setSavingAndSendingMedias(false);
    },
    [medias, updateTagsMediasToQualify, updateStatusMediasToQualify]
  );

  return (
    <div style={{ padding: '16px' }}>
      <Tooltip title={mediaNamesTooltip}>
        <p
          style={{
            margin: 0,
            padding: '12px 14px',
            fontSize: '13px',
            lineHeight: 1.5,
            backgroundColor: BULK_NOTICE_BG,
            color: BULK_NOTICE_TEXT,
            borderRadius: 8,
            cursor: 'help',
          }}
        >
          Les modifications seront appliquées {scopeLabel}.
        </p>
      </Tooltip>

      <div style={{ marginTop: 24, marginBottom: 20 }}>
        <span style={bulkLabelStyle}>Tags</span>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Saisir ou sélectionner des tags"
          options={tagOptions}
          value={tagValues}
          onChange={handleTagsChange}
          searchValue={searchedTag}
          onSearch={setSearchedTag}
          loading={isTagLoading}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? '')
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </div>

      <Divider style={{ margin: '24px 0 16px' }} />

      <div style={editionActionsRowStyle}>
        <div style={editionActionsInnerStyle}>
          <Button
            type="primary"
            block
            style={editionActionButtonStyle}
            onClick={() => handleSave(oldTagValues, tagValues)}
            loading={savingMedias}
            disabled={savingAndSendingMedias}
          >
            {!savingMedias
              ? 'Enregistrer les modifications'
              : 'Enregistrement en cours'}
          </Button>
          <Button
            block
            style={editionActionButtonStyle}
            onClick={() => handleSaveAndSubmit(oldTagValues, tagValues)}
            loading={savingAndSendingMedias}
            disabled={savingMedias}
          >
            {!savingAndSendingMedias
              ? 'Enregistrer et envoyer en validation'
              : 'Enregistrement et envoi en cours'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MediaUpdate = ({ medias = [] }) => {
  const isMultipleSelection = medias.length > 1;
  const isSingleSelection = medias.length === 1;

  const getHeaderTitle = () => {
    if (isMultipleSelection) {
      return 'Édition groupée';
    }
    if (isSingleSelection) {
      return 'Détails et édition';
    }
    return 'Aucun média sélectionné';
  };

  if (medias.length === 0) {
    return (
      <div style={{ padding: '16px', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#999',
          }}
        >
          <p>Sélectionnez un ou plusieurs médias pour voir les détails</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          {getHeaderTitle()}
        </h3>
        {isMultipleSelection && (
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
            {medias.length} médias sélectionnés
          </p>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {isSingleSelection ? (
          <MediaDetailsEdition medias={medias} />
        ) : (
          <Tabs
            defaultActiveKey="details"
            items={[
              {
                key: 'details',
                label: 'Détails',
                children: <MediaDetailsEdition medias={medias} />,
              },
              {
                key: 'edition',
                label: 'Édition',
                children: <BulkEditionPanel medias={medias} />,
              },
            ]}
            style={{ padding: '0 16px' }}
          />
        )}
      </div>
    </div>
  );
};

export default MediaUpdate;
